import {css, html, HTMLTemplateResult, nothing} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {isEmpty, T} from 'ramda';
import {
  BehaviorSubject,
  filter,
  identity,
  interval,
  NEVER,
  startWith,
  takeUntil,
  of,
  map,
  shareReplay,
  Observable,
  tap,
  skip,
} from 'rxjs';
import {observe} from './directives/observe';
import global_css from './global-css';
import {Rectangle} from './interfaces';
import {ri} from './runner-idle';
import {PanelID} from './types/runner-idle';
import {UnlockableLitElement} from './unlockable-lit-element';
import {convertCamelCaseToTitleCase} from './utils';
export interface BoundValue {
  min: number;
  max: number;
  value: number;
}

@customElement('ri-panel')
export class Panel extends UnlockableLitElement {
  // #region Properties (11)

  public static override styles = [
    global_css,
    css`
      :host {
        width: auto;
        height: auto;
        overflow: hidden;
      }
      :host([closed]) {
        visibility: hidden;
      }
      :host .resize-dummy {
        position: absolute;
        overflow: hidden;
        /* pointer-events: none; */
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        resize: both;
        z-index: -1;
      }
      :host([oneline]) .resize-dummy {
        resize: horizontal;
      }
      :host(:not([editing])) .resize-dummy {
        resize: none;
      }
      ::slotted(*) {
        overflow: auto;
        height: 100%;
        width: 100%;
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .header .name {
        font-weight: 600;
      }
      .subtitle {
        font-size: 0.9rem;
        font-weight: initial;
        font-style: oblique;
        color: #0009;
      }
      .header-right,
      .panel-editing {
        display: flex;
      }
      .panel-editing * {
        z-index: 1;
      }
      [hidden] {
        display: none;
      }
      .drag-handle {
        cursor: move;
      }
      .drag-handle-icon {
        font-size: 1.5rem;
        font-weight: bold;
        outline: 2px solid #000;
        border-radius: 4px;
      }
      .content {
        display: flex;
        justify-content: space-between;
        z-index: 1;
        overflow: auto;
        height: calc(100% - 32px);
        width: 100%;
      }
      :host {
        /* height: 100%; */
        width: 100%;
      }
      .container {
        overflow: hidden;
        height: 100%;
      }
      .pin-button.pinned {
        background-color: #000f;
      }
    `,
  ];

  /** The string shown when accessing help. An empty string turns off help for the panel. */
  @property({reflect: true}) public helpContent = '';
  /** Is this panel currently being edited? */
  @property({type: Boolean, reflect: true}) public isBeingEdited = true;
  /** Is this panel currently minimized? */
  @property({type: Boolean, reflect: true}) public isMinimized = false;
  /** Is this panel a single line of text? Changes display. */
  @property({type: Boolean, reflect: true}) public isOneLine = false;
  /** The subtitle of this panel. */
  @property({reflect: true}) public subtitle = '';

  public override baseTags = new Set(['panel']);
  public override name = '';
  public oldShape: Rectangle = {
    height: '',
    width: '',
    left: '',
    top: '',
  };
  public resizeDummy: HTMLTemplateResult;

  // #endregion Properties (11)

  // #region Constructors (1)

  constructor() {
    super();
    // this.tabIndex = -1;
    // console.log(`Constructing Panel ${this.id} ${this.name}`);
    this.new = false;
    // this.hidden = true;
    this.unlock$ = of(true);
    // Bloated, do on parent once instead of for each panel, but this works
    let resizeLayoutTimeout: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLElement)) continue;
        clearTimeout(resizeLayoutTimeout);
        const rect = entry.target.getBoundingClientRect();
        const widthStep = Math.round((ri.masonryRoot?.offsetWidth ?? 0) / 10);
        const heightStep = Math.round((ri.masonryRoot?.offsetHeight ?? 0) / 10);
        this.style.setProperty(
          'width',
          `${Math.max(1, Math.round(rect.width / widthStep) * widthStep)}px`
        );
        this.style.setProperty(
          'height',
          `${Math.max(1, Math.round(rect.height / heightStep) * heightStep)}px`
        );
        this.style.setProperty(
          'left',
          `${Math.max(1, Math.round(rect.left / widthStep) * widthStep)}px`
        );
        this.style.setProperty(
          'top',
          `${Math.max(1, Math.round(rect.top / heightStep) * heightStep)}px`
        );
        resizeLayoutTimeout = setTimeout(() => {
          if (!(entry.target instanceof HTMLElement)) return;
          if (!ri.isDragging) {
            ri.masonry?.shiftLayout();
            entry.target.style.width = '';
            entry.target.style.height = '';
          } else {
            ri.masonry?.layout();
          }
        }, 250);
      }
    });
    this.mouseUp$.subscribe(() => {
      resizeLayoutTimeout = setTimeout(() => {
        if (!ri.isDragging) {
          const dummy = this.shadowRoot?.querySelector('.resize-dummy');
          if (!(dummy instanceof HTMLElement)) return;
          ri.masonry?.shiftLayout();
          dummy.style.width = '';
          dummy.style.height = '';
        } else {
          ri.masonry?.layout();
        }
      }, 250);
    });
    this.resizeDummy = html`<div class="resize-dummy"></div>`;
    this.click$.subscribe(() => {
      ri.focusPanel(this);
    });

    setTimeout(() => {
      resizeObserver.observe(
        this.shadowRoot?.querySelector('.resize-dummy') ?? this
      );
      this.displayName = this.name;
    });
  }

  // #endregion Constructors (1)

  // #region Public Methods (2)

  public override setup() {
    // console.log(`Setup Panel ${this.name}`);
    super.setup();
    // Unlock the panel when this unlocks. Move to runneridle?
    this.unlock$
      .pipe(
        skip(1),
        filter(identity),
        map(() => this.id as PanelID)
      )
      .subscribe(ri.unlockPanel.bind(ri));
  }

  public hide(): void {
    this.toggleAttribute('closed', true);
  }

  public togglePin(force: boolean | undefined = undefined) {
    if (this.classList.toggle('pinned', force)) {
      ri.masonry?.stamp(this);
      ri.masonry?.reloadItems();
    } else {
      ri.masonry?.unstamp(this);
      ri.masonry?.reloadItems();
    }
    this.requestUpdate();
  }

  // #endregion Public Methods (2)

  // #region Protected Methods (3)

  protected onFullscreenToggle() {
    const oldWidth = this.style.width;
    const oldHeight = this.style.height;
    const oldTop = this.style.top;
    const oldLeft = this.style.left;
    if (this.classList.toggle('fullscreen')) {
      this.oldShape.width = oldWidth;
      this.oldShape.height = oldHeight;
      this.oldShape.top = oldTop;
      this.oldShape.left = oldLeft;
    } else {
      this.style.width = this.oldShape.width;
      this.style.height = this.oldShape.height;
      this.style.top = this.oldShape.top;
      this.style.left = this.oldShape.left;
    }
  }

  protected onMinimize() {
    const oldWidth = this.style.width;
    const oldHeight = this.style.height;
    const oldTop = this.style.top;
    const oldLeft = this.style.left;
    if (this.classList.toggle('minimized')) {
      this.oldShape.width = oldWidth;
      this.oldShape.height = oldHeight;
      this.oldShape.top = oldTop;
      this.oldShape.left = oldLeft;
    } else {
      this.style.width = this.oldShape.width;
      this.style.height = this.oldShape.height;
      this.style.top = this.oldShape.top;
      this.style.left = this.oldShape.left;
    }
    this.refresh();
  }
  settings: Record<string, boolean | BoundValue | object> = {};
  settingsVisible = false;
  toggleSettings(force?: boolean) {
    if (force === undefined) {
      this.settingsVisible = !this.settingsVisible;
    } else {
      this.settingsVisible = force;
    }
    this.requestUpdate();
  }

  protected override render(
    content = html`<div class="content"><slot></slot></div>`
  ) {
    return html`
      ${this.resizeDummy}
      <div class="header drag-handle">
        <div class="name">${this.name}</div>
        <div class="subtitle">${this.subtitle}</div>
        ${this.isOneLine ? content : nothing}
        <div class="header-right">
          ${!isEmpty(this.settings)
            ? html`<button
                class="settings"
                @click=${() => this.toggleSettings()}
              >
                ⚙
              </button>`
            : nothing}
          ${this.helpContent
            ? html`<help-icon .content=${this.helpContent}></help-icon>`
            : nothing}
          <div
            class=${`panel-editing ${
              this.isBeingEdited ? '' : 'collapse-horizontal'
            }`}
          >
            <button
              class="minimize"
              @click=${this.onMinimize}
              ?hidden=${!this.classList.contains('pinned')}
            >
              _
            </button>
            <button @click=${this.onFullscreenToggle}>⤢</button>
            <button @click=${() => this.hide()}>X</button>
            <!-- <div class="drag-handle drag-handle-icon big">✥</div> -->
          </div>
        </div>
      </div>
      <div class="settings" ?hidden=${!this.settingsVisible}>
        ${(
          Object.entries(this.settings ?? {}) as [
            string,
            boolean | BoundValue
          ][]
        ).map(([pref, value]) => {
          switch (true) {
            case typeof value === 'boolean':
              return html`<button
                @click=${() => {
                  this.settings[pref] = !this.settings[pref];
                  ri.requestUpdate();
                  this.requestUpdate();
                }}
                class="${value ? 'toggled' : ''}"
              >
                ${convertCamelCaseToTitleCase(pref)}
              </button>`;
            case typeof value === 'object' &&
              'value' in value &&
              'min' in value &&
              'max' in value:
              return html` <label for=${pref}>
                ${convertCamelCaseToTitleCase(pref)}
                <input
                  id=${pref}
                  type="range"
                  value=${value.value}
                  min=${value.min}
                  max=${value.max}
                  step="0.1"
                  @input=${(event: InputEvent) => {
                    this.settings[pref].value =
                      event.target?.value ?? value.value;
                  }}
              /></label>`;
            default:
              return nothing;
          }
        })}
      </div>
      <div class="container">${this.isOneLine ? nothing : content}</div>
    `;
  }

  // #endregion Protected Methods (3)

  // #region Private Methods (1)

  private refresh() {
    ri.masonry?.reloadItems();
    if (!ri.isDragging) ri.masonry?.layout();
  }

  // #endregion Private Methods (1)
}
